import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faList,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import Swal from "sweetalert2";
import { Col } from "reactstrap";
import { APISD } from "../../../../../../../config/apisd";
import ToastError from "../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../NotificationToast/ToastSuccess";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/update";

import "bulma/css/bulma.css";
import "../../../../../../../index.css";

const pageStyles = `
  .tipe-ujian-search-section {
    margin-bottom: 14px;
  }

  .tipe-ujian-table {
    min-width: 560px;
  }

  .tipe-ujian-table .tipe-ujian-col-number {
    width: 78px;
  }

  .tipe-ujian-table .tipe-ujian-col-name {
    width: auto;
  }

  .tipe-ujian-table .tipe-ujian-col-action {
    width: 130px;
    text-align: center !important;
  }

  .tipe-ujian-name-text {
    display: block;
    width: 100%;
    color: #24314e;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.5;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: normal;
  }

  @media (max-width: 780px) {
    .tipe-ujian-table {
      min-width: 520px;
    }

    .tipe-ujian-page .page-header-top {
      align-items: flex-start !important;
      flex-direction: column;
      gap: 14px;
    }

    .tipe-ujian-page .page-header-top .ml-auto {
      width: 100%;
      margin-left: 0 !important;
    }

    .tipe-ujian-page .sims-header-button {
      width: 100%;
      justify-content: center;
    }
  }
`;

export default function TipeUjian() {
  document.title = "Tipe Ujian";

  const token = localStorage.getItem("token");
  const ascending = 1;

  const [getData, setGetData] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFetching, setIsFetching] = useState(false);

  const [id, setId] = useState("");
  const [nameUpdate, setNameUpdate] = useState("");
  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const GetResponseData = useCallback(async () => {
    setSearchKeyword("");
    setIsFetching(true);

    try {
      const response = await APISD.get(
        `/api/learning-types?page=1&limit=1000&ascending=${ascending}&search=`,
        fetchParams
      );

      if (response?.status === 200) {
        setGetData(response?.data?.data || []);
      }
    } catch (error) {
      setGetData([]);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal memuat data tipe ujian",
      });
    } finally {
      setIsFetching(false);
    }
  }, [ascending, fetchParams]);

  useEffect(() => {
    GetResponseData();
  }, [GetResponseData]);

  const deleteById = (learningTypeId) => {
    Swal.fire({
      title: "Apakah kamu yakin?",
      text: "Data yang dihapus tidak dapat dikembalikan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, Hapus",
      cancelButtonText: "Batal",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          const response = await APISD.delete(
            `/api/learning-types/${learningTypeId}`,
            fetchParams
          );

          if (response?.data?.error) {
            throw new Error(response?.data?.message);
          }

          return response;
        } catch (error) {
          Swal.showValidationMessage(
            error?.response?.data?.message ||
              error?.message ||
              "Gagal menghapus data"
          );

          return false;
        }
      },
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      ToastSuccess.fire({
        icon: "success",
        title: "Data berhasil dihapus",
      });

      GetResponseData();
    });
  };

  const filteredData = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    const source = Array.isArray(getData) ? getData : [];

    if (!keyword) {
      return source;
    }

    return source.filter((item) =>
      String(item?.name_learning || "")
        .toLowerCase()
        .includes(keyword)
    );
  }, [getData, searchKeyword]);

  const totalRows = filteredData.length;

  const totalPages = useMemo(() => {
    const pages = Math.ceil(totalRows / Math.max(1, rowsPerPage));
    return pages > 0 ? pages : 1;
  }, [rowsPerPage, totalRows]);

  useEffect(() => {
    setTablePage(1);
  }, [searchKeyword, getData]);

  useEffect(() => {
    if (tablePage > totalPages) {
      setTablePage(totalPages);
    }

    if (tablePage < 1) {
      setTablePage(1);
    }
  }, [tablePage, totalPages]);

  const paginatedData = useMemo(() => {
    const safeRows = Math.max(1, rowsPerPage);
    const start = (tablePage - 1) * safeRows;

    return filteredData.slice(start, start + safeRows);
  }, [filteredData, rowsPerPage, tablePage]);

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    const pages = [];

    if (totalPages <= maxButtons) {
      for (let page = 1; page <= totalPages; page += 1) {
        pages.push(page);
      }

      return pages;
    }

    const left = Math.max(1, tablePage - 1);
    const right = Math.min(totalPages, tablePage + 1);

    pages.push(1);

    if (left > 2) {
      pages.push("...");
    }

    for (let page = left; page <= right; page += 1) {
      if (page !== 1 && page !== totalPages) {
        pages.push(page);
      }
    }

    if (right < totalPages - 1) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages.filter(
      (value, index, array) => array.indexOf(value) === index
    );
  }, [tablePage, totalPages]);

  const goToPage = (nextPage) => {
    const safePage = Math.min(
      Math.max(1, Number(nextPage) || 1),
      totalPages
    );

    setTablePage(safePage);
  };

  const viewModalUpdate = (row) => {
    setId(row?.id || "");
    setNameUpdate(row?.name_learning || "");
    setModalUpdate(true);
  };

  const startRow =
    totalRows === 0
      ? 0
      : Math.min((tablePage - 1) * rowsPerPage + 1, totalRows);

  const endRow = Math.min(tablePage * rowsPerPage, totalRows);

  return (
    <div className="body sims-page">
      <style>{pageStyles}</style>

      {modalAdd && (
        <ModalAdd
          GetResponseData={GetResponseData}
          show={modalAdd}
          onHide={() => setModalAdd(false)}
        />
      )}

      {modalUpdate && (
        <ModalUpdate
          GetResponseData={GetResponseData}
          id={id}
          nameUpdate={nameUpdate}
          show={modalUpdate}
          onHide={() => {
            setModalUpdate(false);
            setId("");
            setNameUpdate("");
          }}
        />
      )}

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faList} />
            </span>

            <h4 className="page-title-text">Tipe Ujian</h4>
          </div>

          <div className="ml-auto">
            <button
              type="button"
              onClick={() => setModalAdd(true)}
              className="btn btn-create sims-header-button"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Tambah Tipe Ujian</span>
            </button>
          </div>
        </div>

        <nav
          aria-label="breadcrumb"
          className="sims-breadcrumb-nav mt-4"
        >
          <ol className="sims-breadcrumb">
            <li className="sims-breadcrumb-item">
              <Link to="/matpels">Mata Pelajaran</Link>
            </li>

            <li className="sims-breadcrumb-separator">/</li>

            <li
              className="sims-breadcrumb-item active"
              aria-current="page"
            >
              Tipe Ujian
            </li>
          </ol>
        </nav>
      </div>

      <div className="body-content sims-content">
        {isFetching ? (
          <div className="sims-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        ) : (
          <Col xl="12" sm="12" className="sims-column">
            <div className="sims-search-wrap mb-3">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="sims-search-icon"
              />

              <input
                type="text"
                className="sims-search-input"
                placeholder="Cari tipe ujian..."
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
              />
            </div>

            <div className="sims-table-card">
              <div className="sims-table-scroll">
                <table className="sims-table">
                  <thead>
                    <tr>
                      <th className="sims-col-number">
                        No
                      </th>
                      <th>Tipe Ujian</th>
                      <th className="sims-col-action">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row, index) => (
                        <tr key={row?.id || index}>
                          <td className="sims-col-number">
                            <span className="sims-number-badge">
                              {(tablePage - 1) * rowsPerPage + index + 1}
                            </span>
                          </td>

                          <td>
                            <span className="tipe-ujian-name-text">
                              {row?.name_learning || "-"}
                            </span>
                          </td>

                          <td className="sims-col-action ">
                            <div className="sims-action-wrap">
                              <button
                                type="button"
                                className="sims-action-button sims-action-edit"
                                onClick={() => viewModalUpdate(row)}
                                title="Ubah tipe ujian"
                                aria-label={`Ubah ${
                                  row?.name_learning || "tipe ujian"
                                }`}
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </button>

                              <button
                                type="button"
                                className="sims-action-button sims-action-delete"
                                onClick={() => deleteById(row?.id)}
                                title="Hapus tipe ujian"
                                aria-label={`Hapus ${
                                  row?.name_learning || "tipe ujian"
                                }`}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="sims-empty-state">
                          <div className="sims-empty-state-content">
                            Data tidak ditemukan
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {filteredData.length > 0 && (
                <div className="sims-pagination">
                  <div className="sims-pagination-left">
                    <span className="sims-page-info">
                      Menampilkan <b>{startRow}</b>-<b>{endRow}</b> dari{" "}
                      <b>{totalRows}</b> data
                    </span>

                    <select
                      className="sims-page-size"
                      value={rowsPerPage}
                      onChange={(event) => {
                        const value = Number(event.target.value) || 10;
                        setRowsPerPage(value);
                        setTablePage(1);
                      }}
                    >
                      <option value={10}>10 / halaman</option>
                      <option value={25}>25 / halaman</option>
                      <option value={50}>50 / halaman</option>
                      <option value={100}>100 / halaman</option>
                    </select>
                  </div>

                  <div className="sims-pagination-right">
                    <button
                      type="button"
                      className="sims-page-nav"
                      onClick={() => goToPage(tablePage - 1)}
                      disabled={tablePage <= 1}
                      aria-label="Halaman sebelumnya"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                      <span>Sebelumnya</span>
                    </button>

                    <div className="sims-page-numbers">
                      {pageNumbers.map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`dots-${index}`}
                            className="sims-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            type="button"
                            key={`page-${page}`}
                            className={`sims-page-number ${
                              page === tablePage ? "active" : ""
                            }`}
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      type="button"
                      className="sims-page-nav"
                      onClick={() => goToPage(tablePage + 1)}
                      disabled={tablePage >= totalPages}
                      aria-label="Halaman berikutnya"
                    >
                      <span>Selanjutnya</span>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Col>
        )}
      </div>
    </div>
  );
}
